import { useEffect, useRef, useState } from 'react';
import { useHits } from 'react-instantsearch';

import { useHeaderOffSet } from '@/hooks/useHeaderOffSet';
import { useListingState } from '@/hooks/useListingState';
import { useIsMdDown } from '@/hooks/useMedia';
import { useConfigProvider } from '@/providers/ConfigProvider';

import { FiltersApplied } from './components/FiltersApplied/FiltersApplied';
import { FiltersButton } from './components/FiltersButton';
import type { FiltersModalProps } from './components/FiltersModal';
import { FiltersModal } from './components/FiltersModal';
import { HitsSummary } from './components/HitsSummary';
import { ListingFiltersProvider } from './ListingFiltersProvider';
import type { ListingFiltersType } from './ListingFiltersProviderContext';
import type { ListingFiltersVariant } from './types';

import * as S from './ListingFilters.styles';

interface ListingFiltersProps extends FiltersModalProps {
  /**
   * Whether the filtered search results are ready to display on the search page
   * Used to delay showing counts until the search with URL filters completes,
   * preventing a flash of unfiltered counts.
   */
  filteredResultsReady?: boolean;
  /**
   * Force mount the filter widgets even if the modal hasn't been opened.
   * This is needed when we have URL filters that need widgets to be registered
   * with InstantSearch for the search query to include those filters.
   */
  forceMount?: boolean;
  hideIfNoResults?: boolean;
  initialResults?: any;
  listingFiltersType?: ListingFiltersType;
  shouldUpdateVariant?: boolean;
  showMaterialsFilter?: boolean;
}

export const ListingFilters = ({
  collectionData,
  filteredResultsReady = true,
  forceMount = false,
  hideIfNoResults = false,
  initialResults,
  listingFiltersType = 'PLP',
  shouldUpdateVariant = false,
  showCollectionFilter = false,
  showMaterialsFilter = false,
}: ListingFiltersProps) => {
  const { listingType } = useListingState();
  const { headerRef } = useConfigProvider();
  const isMobile = useIsMdDown();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [colorVariant, setColorVariant] = useState<ListingFiltersVariant>(
    shouldUpdateVariant ? 'defaultInverted' : 'default',
  );
  const [isClient, setIsClient] = useState(false);

  let { results } = useHits();

  if (results?.nbHits === 0) {
    results = {
      ...results,
      ...initialResults,
    };
  }

  const top = useHeaderOffSet();

  // Ensure we're on the client side before using media queries and DOM measurements
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!shouldUpdateVariant) {
      return;
    }

    const scrollHandler = () => {
      if (
        (wrapperRef?.current?.getBoundingClientRect().top || 0) >
        (headerRef?.current?.offsetHeight || 0)
      ) {
        setColorVariant('defaultInverted');
      } else {
        setColorVariant('default');
      }
    };

    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, []);

  // Don't render anything until we're on the client side to avoid hydration mismatches
  if (!isClient) {
    return null;
  }

  return (
    <ListingFiltersProvider listingFiltersType={listingFiltersType}>
      <S.FiltersWrapper
        ref={wrapperRef}
        data-test-id='ListingFiltersWrapper'
        shouldUpdateVariant={shouldUpdateVariant}
        top={top}
        variant={colorVariant}
      >
        <S.RelativeContainer $type={listingFiltersType}>
          <S.Wrapper gridGap={2} justifyContent='space-between'>
            <S.FiltersColumn
              alignItems='center'
              data-test-id='ListingFiltersColumn1'
              gridGap={2}
              justifyContent='space-between'
            >
              {filteredResultsReady &&
                !isMobile &&
                results &&
                results?.nbHits > 0 && (
                  <HitsSummary
                    data-test-id='ListingFiltersHitsSummary'
                    initialResults={results}
                    listingType={listingType}
                    variant={colorVariant}
                  />
                )}
            </S.FiltersColumn>

            <S.FiltersColumn
              $hidden={hideIfNoResults && results?.nbHits === 0}
              alignItems='flex-start'
              data-test-id='ListingFiltersColumn2'
              flexDirection='column'
              gridGap={2}
              justifyContent='space-between'
            >
              <S.Filters data-test-id='ListingFilters'>
                {['WATCH', 'STRAP', 'STORE', 'EYEWEAR']?.includes(
                  listingType,
                ) && <FiltersButton />}
                {!isMobile && <FiltersApplied variant={colorVariant} />}
                {filteredResultsReady &&
                  isMobile &&
                  results &&
                  results?.nbHits > 0 && (
                    <HitsSummary
                      data-test-id='ListingFiltersHitsSummary'
                      initialResults={results}
                      listingType={listingType}
                      variant={colorVariant}
                    />
                  )}
              </S.Filters>
              {isMobile && <FiltersApplied variant={colorVariant} />}
            </S.FiltersColumn>
          </S.Wrapper>
        </S.RelativeContainer>
      </S.FiltersWrapper>
      <FiltersModal
        collectionData={collectionData}
        data-test-id='ListingFiltersModal'
        forceMount={forceMount}
        showCollectionFilter={showCollectionFilter}
        showMaterialsFilter={showMaterialsFilter}
      />
    </ListingFiltersProvider>
  );
};

ListingFilters.displayName = 'ListingFilters';
